import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaUserShield, FaSearch } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import permisosApi from '../../../services/api/permisosApi';
import 'react-toastify/dist/ReactToastify.css';

const GestionRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await permisosApi.getRoles();
      if (response.data) {
        setRoles(Array.isArray(response.data) ? response.data : [response.data]);
      }
    } catch (error) {
      console.error('Error al cargar roles:', error);
      toast.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const handleShow = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        nombre: role.nombre || '',
        descripcion: role.descripcion || ''
      });
    } else {
      setEditingRole(null);
      setFormData({
        nombre: '',
        descripcion: ''
      });
    }
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData({
      nombre: '',
      descripcion: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      toast.error('El nombre del rol es obligatorio');
      return;
    }

    try {
      if (editingRole) {
        // Actualizar rol
        await permisosApi.updateRole(editingRole.id, formData);
        toast.success('Rol actualizado correctamente');
      } else {
        // Crear rol
        await permisosApi.createRole(formData);
        toast.success('Rol creado correctamente');
      }
      handleClose();
      loadRoles();
    } catch (error) {
      console.error('Error al guardar rol:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar rol');
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('¿Está seguro de desactivar este rol?')) {
      return;
    }

    try {
      await permisosApi.deleteRole(roleId);
      toast.success('Rol desactivado correctamente');
      loadRoles();
    } catch (error) {
      console.error('Error al desactivar rol:', error);
      toast.error(error.response?.data?.detail || 'Error al desactivar rol');
    }
  };

  const filteredRoles = roles.filter(role =>
    role.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      <ToastContainer />
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <FaUserShield className="me-2" />
                <strong>Gestión de Roles</strong>
              </div>
              <Button variant="primary" onClick={() => handleShow()}>
                <FaPlus className="me-2" />
                Nuevo Rol
              </Button>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={4}>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Buscar roles..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Descripción</th>
                      <th>Permisos</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoles.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No hay roles registrados
                        </td>
                      </tr>
                    ) : (
                      filteredRoles.map((role) => (
                        <tr key={role.id}>
                          <td>{role.id}</td>
                          <td><strong>{role.nombre}</strong></td>
                          <td>{role.descripcion || '-'}</td>
                          <td>
                            {role.permisos && role.permisos.length > 0 ? (
                              <Badge bg="info">{role.permisos.length} permisos</Badge>
                            ) : (
                              <Badge bg="secondary">Sin permisos</Badge>
                            )}
                          </td>
                          <td>
                            <Badge bg={role.activo ? 'success' : 'secondary'}>
                              {role.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleShow(role)}
                              >
                                <FaEdit />
                              </Button>
                              {role.activo && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(role.id)}
                                >
                                  <FaTrash />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar rol */}
      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Rol *</Form.Label>
              <Form.Control
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                placeholder="Ej: supervisor_financiero"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                placeholder="Descripción del rol..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingRole ? 'Actualizar' : 'Crear'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default GestionRoles;
